import React, { useEffect, useState } from "react";
import { KeyRound, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

export const LOGIN_BADGE = {
  own: { label: "Using own password", cls: "bg-green-100 text-green-700" },
  not_changed: { label: "Not changed password yet", cls: "bg-amber-100 text-amber-700" },
  cant_login: { label: "Can't log in", cls: "bg-red-100 text-red-700" },
  inactive: { label: "Inactive 30+ days", cls: "bg-slate-200 text-slate-600" },
};

export const ACCT_FILTERS = [
  { key: "all", label: "All" },
  { key: "incomplete", label: "Profile incomplete", test: (s) => (s.completion ?? 100) < 100 },
  { key: "not_changed", label: "Not changed password", test: (s) => s.login_status === "not_changed" },
  { key: "cant_login", label: "Can't log in", test: (s) => s.login_status === "cant_login" },
  { key: "inactive", label: "Inactive", test: (s) => s.login_status === "inactive" },
];

export const AccountBadge = ({ s }) => {
  const b = LOGIN_BADGE[s.login_status];
  if (!b) return null;
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${b.cls}`} data-testid={`acct-badge-${s.id}`}>{b.label}</span>;
};

export const CompletionBar = ({ pct, testid }) => (
  <div className="flex items-center gap-1.5" data-testid={testid}>
    <div className="h-1.5 w-14 rounded-full bg-slate-200 overflow-hidden"><div className={`h-full ${pct === 100 ? "bg-green-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${pct}%` }} /></div>
    <span className="text-[10px] font-semibold text-slate-500">{pct}%</span>
  </div>
);

export function AccountPanel({ s, onRequested }) {
  const [busy, setBusy] = useState(false);
  const request = async () => {
    setBusy(true);
    try { await api.post(`/teacher/students/${s.id}/reset-request`, {}); toast.success("Sent to admin — Password Reset"); onRequested && onRequested(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3" data-testid="teacher-account-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="font-bold text-slate-900">Profile &amp; login</h4>
        <AccountBadge s={s} />
      </div>
      <div className="flex items-center gap-3"><CompletionBar pct={s.completion ?? 0} testid="teacher-account-completion" /><span className="text-xs text-slate-500">Last login: {s.last_login ? formatDate(s.last_login) : "Never"}</span></div>
      {s.missing?.length > 0 ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900" data-testid="teacher-account-missing"><AlertTriangle className="h-3.5 w-3.5 inline mr-1" />Missing: {s.missing.join(", ")}</div>
      ) : <p className="text-xs text-green-700" data-testid="teacher-account-complete">Profile is complete.</p>}
      {s.reset_requested ? (
        <p className="text-xs font-semibold text-blue-700" data-testid="teacher-reset-requested">Password reset requested — waiting for admin.</p>
      ) : (
        <Button size="sm" variant="outline" onClick={request} disabled={busy} data-testid="teacher-reset-request-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1" /> Ask admin to reset password</>}</Button>
      )}
    </div>
  );
}

export function AccountSummary() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/teacher/students").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  if (!rows || !rows.length) return null;
  const n = (k) => rows.filter(ACCT_FILTERS.find((f) => f.key === k).test).length;
  const items = [["incomplete", "profile incomplete", "bg-amber-50 text-amber-800 ring-amber-200"], ["not_changed", "haven't set password", "bg-orange-50 text-orange-800 ring-orange-200"], ["cant_login", "can't log in", "bg-red-50 text-red-700 ring-red-200"]].filter(([k]) => n(k) > 0);
  if (!items.length) return null;
  return (
    <div className="w-full flex flex-wrap gap-2" data-testid="teacher-account-summary">
      {items.map(([k, label, cls]) => <span key={k} className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${cls}`} data-testid={`teacher-summary-${k}`}>{n(k)} {label}</span>)}
    </div>
  );
}
